import type {
  AnimationBindingResolver,
  AnimationClipData,
  AnimationPlayerHandle,
  AnimationPlayOptions,
  AnimationService,
  AnimationTargetRef,
} from '@/shared/types/animation'
import type { Disposable } from '@/shared/types/core'
import { AnimationBindingRegistry } from './AnimationBindingRegistry'
import { AnimationPlayerImpl } from './AnimationPlayer'
import { validateAnimationClip } from './validation'

export interface AnimationServiceOptions {
  onVisualChange?: (reason: string, detail?: string) => void
}

export class AnimationServiceImpl implements AnimationService, Disposable {
  private readonly registry = new AnimationBindingRegistry()
  private readonly clips = new Map<string, AnimationClipData>()
  private readonly players = new Map<string, AnimationPlayerImpl>()
  private readonly skipNextUpdatePlayers = new Set<string>()
  private nextClipId = 1
  private nextPlayerId = 1

  constructor(private readonly options: AnimationServiceOptions = {}) {}

  registerClip(clip: AnimationClipData): string {
    validateAnimationClip(clip)
    const id = clip.id ?? `clip-${this.nextClipId++}`
    this.clips.set(id, clip)
    return id
  }

  unregisterClip(id: string): boolean {
    return this.clips.delete(id)
  }

  getClip(id: string): AnimationClipData | undefined {
    return this.clips.get(id)
  }

  play(
    clipOrId: AnimationClipData | string,
    options: AnimationPlayOptions = {}
  ): AnimationPlayerHandle {
    const clip = typeof clipOrId === 'string' ? this.clips.get(clipOrId) : clipOrId
    if (!clip) {
      throw new Error(`Animation clip "${clipOrId}" is not registered`)
    }

    const id = options.id ?? `player-${this.nextPlayerId++}`
    if (this.players.has(id)) {
      throw new Error(`Animation player "${id}" already exists`)
    }

    const player = new AnimationPlayerImpl(id, clip, this.registry, options)
    this.players.set(id, player)
    let stopSubscription: Disposable | null = null
    stopSubscription = player.on('stop', () => {
      this.players.delete(id)
      stopSubscription?.dispose()
      stopSubscription = null
      this.notifyVisualChange('animation.stopped', id)
    })
    if (options.autoplay !== false) {
      player.play()
      this.notifyVisualChange('animation.started', id)
    }
    return player
  }

  pause(id: string): boolean {
    const player = this.players.get(id)
    if (!player) {
      return false
    }
    player.pause()
    this.notifyVisualChange('animation.paused', id)
    return true
  }

  resume(id: string): boolean {
    const player = this.players.get(id)
    if (!player) {
      return false
    }
    player.resume()
    this.notifyVisualChange('animation.resumed', id)
    return true
  }

  stop(id: string): boolean {
    const player = this.players.get(id)
    if (!player) {
      return false
    }
    player.dispose()
    this.players.delete(id)
    this.skipNextUpdatePlayers.delete(id)
    return true
  }

  sampleAt(id: string, time: number): boolean {
    const player = this.players.get(id)
    if (!player) {
      return false
    }
    player.sampleAt(time)
    this.skipNextUpdatePlayers.add(id)
    this.notifyVisualChange('animation.sampled', id)
    return true
  }

  seekAndApply(id: string, time: number): boolean {
    const player = this.players.get(id)
    if (!player) {
      return false
    }
    player.seekAndApply(time)
    this.skipNextUpdatePlayers.add(id)
    this.notifyVisualChange('animation.seeked', id)
    return true
  }

  seek(id: string, time: number): boolean {
    const player = this.players.get(id)
    if (!player) {
      return false
    }
    player.seek(time)
    this.notifyVisualChange('animation.seeked', id)
    return true
  }

  setSpeed(id: string, speed: number): boolean {
    const player = this.players.get(id)
    if (!player) {
      return false
    }
    player.setSpeed(speed)
    this.notifyVisualChange('animation.speedChanged', id)
    return true
  }

  stopByTarget(targetRef: AnimationTargetRef): number {
    const resolved = this.registry.resolve(targetRef)
    const resolvedKey = resolved?.key
    let stopped = 0
    for (const [id, player] of Array.from(this.players.entries())) {
      if ((resolvedKey && player.hasTargetKey(resolvedKey)) || player.hasTargetRef(targetRef)) {
        player.dispose()
        this.players.delete(id)
        this.skipNextUpdatePlayers.delete(id)
        stopped += 1
      }
    }
    if (stopped > 0) {
      this.notifyVisualChange('animation.stoppedByTarget', targetRef.kind)
    }
    return stopped
  }

  update(deltaSeconds: number): void {
    for (const [id, player] of Array.from(this.players.entries())) {
      if (this.skipNextUpdatePlayers.delete(id)) {
        continue
      }
      player.update(deltaSeconds)
      if (player.state === 'completed' || player.state === 'stopped') {
        player.dispose()
        this.players.delete(id)
        this.skipNextUpdatePlayers.delete(id)
        this.notifyVisualChange('animation.completed', id)
      }
    }
  }

  hasPlayingPlayers(): boolean {
    for (const player of this.players.values()) {
      if (player.state === 'playing') {
        return true
      }
    }
    return false
  }

  registerBinding(kind: string, resolver: AnimationBindingResolver): Disposable {
    return this.registry.register(kind, resolver)
  }

  dispose(): void {
    for (const player of this.players.values()) {
      player.dispose()
    }
    this.players.clear()
    this.skipNextUpdatePlayers.clear()
    this.clips.clear()
    this.registry.clear()
  }

  private notifyVisualChange(reason: string, detail?: string): void {
    this.options.onVisualChange?.(reason, detail)
  }
}
